// lib/authService.ts
'use client';

import { 
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  signInWithRedirect as firebaseSignInWithRedirect,
  signInWithCustomToken,
  GoogleAuthProvider,
  getRedirectResult,
  signOut as firebaseSignOut,
  User,
  onAuthStateChanged
} from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth } from './firebase';

// Constants
const SESSION_KEY = 'user_session';
export const CURRENT_SESSION_KEY = 'current_session'; // Export this constant
const TOKEN_KEY = 'token';

// Types
export interface UserSession {
  uid: string;
  email: string | null;
  name: string | null;
  token: string;
  tokenResult?: any; // Store the full token result if needed
  role?: string;
  tokenTimestamp?: number;
}

/**
 * Save user session to localStorage with consistent keys
 */
export const saveUserSession = (session: UserSession): void => {
  if (!session || !session.uid) return;

  try {
    // Add timestamp before saving
    const sessionToSave = {
      ...session,
      tokenTimestamp: Date.now()
    };
    // Save the full session object with timestamp
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionToSave));

    // CURRENT_SESSION_KEY should be set explicitly elsewhere when the *backend* session ID is known.
    // Do not automatically set it to the Firebase UID here.
    // localStorage.setItem(CURRENT_SESSION_KEY, session.uid); // Removed this line
    localStorage.setItem(TOKEN_KEY, session.token); // Keep saving the token

    console.log('Session object saved for UID:', session.uid);
  } catch (error) {
    console.error('Error saving user session:', error);
  }
};

/**
 * Get the current user session from localStorage
 */
export const getUserSession = (): UserSession | null => {
  try {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;
    
    return JSON.parse(sessionStr) as UserSession;
  } catch (error) {
    console.error('Failed to parse user session:', error);
    return null;
  }
};

/**
 * Clear all auth-related data from localStorage
 */
export const clearAuthData = (): void => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CURRENT_SESSION_KEY);
    localStorage.removeItem('authMethod');
    localStorage.removeItem('viewing_as_child');
    localStorage.removeItem('parent_id');
    localStorage.removeItem('parent_name');
    // Remove additional role and session-related keys that may linger
    localStorage.removeItem('user_role');
    localStorage.removeItem('role');
    localStorage.removeItem('calendly-store'); // In case our app inadvertently wrote it
    localStorage.removeItem('calendly-internal-store');
    localStorage.removeItem('user_name');
    localStorage.removeItem('parentEnrollmentMessage');
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

/**
 * Sign in with email and password
 */
export const signInWithEmailAndPassword = async (
  email: string, 
  password: string
): Promise<UserSession> => {
  // Clear any previous auth state first
  await signOut();
  
  console.log("Attempting email/password sign-in");
  
  try {
    // Use Firebase's email/password auth
    const userCredential = await firebaseSignInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Get fresh token with custom claims
    const additionalClaims = {
      student_id: localStorage.getItem('viewing_as_child') || undefined
    };
    const tokenResult = await user.getIdTokenResult(true);
    const token = await auth.currentUser?.getIdToken(true, additionalClaims);
    
    // Get user details from Firestore
    const userDetails = await getUserDetailsFromFirestore(user);
    
    // Create session
    const userSession: UserSession = {
      uid: user.uid,
      email: user.email,
      name: user.displayName || userDetails?.name || null,
      token: token,
      role: userDetails?.role
    };
    
    // Save session
    saveUserSession(userSession);
    
    console.log("Authentication successful");
    return userSession;
  } catch (error: any) {
    console.error("Authentication error:", error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
    clearAuthData();
    console.log("User signed out");
  } catch (error) {
    console.error("Sign out error:", error);
    throw error;
  }
};

/**
 * Set up a listener for auth state changes
 */
export const setupAuthStateListener = (
  callback: (user: User | null) => void
): (() => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Setup auth listener used by the session provider
 * This matches the signature expected by useSession
 */
export const setupAuthListener = (
  setSession: (session: UserSession | null) => void,
  setError: (error: string | null) => void,
  setIsLoading: (loading: boolean) => void
): (() => void) => {
  return onAuthStateChanged(auth, async (user) => {
    console.log("Auth state changed:", user ? `User ${user.uid}` : "No user");
    
    if (!user) {
      setSession(null);
      setIsLoading(false);
      return;
    }
    
    try {
      // Get fresh token for signed-in user
      // Get custom claims including student_id if viewing as parent
      const additionalClaims = {
        student_id: localStorage.getItem('viewing_as_child') || undefined
      };
      const tokenResult = await user.getIdTokenResult(true);
      const tokenString = await auth.currentUser?.getIdToken(true, additionalClaims);
      
      if (!tokenString) {
        throw new Error('Failed to get authentication token');
      }
  
      // Get user details from Firestore
      const userDetails = await getUserDetailsFromFirestore(user);
      
      // Create session object with token string
      const userSession: UserSession = {
        uid: user.uid,
        email: user.email || '',
        name: user.displayName || userDetails?.name || '',
        token: tokenString,
        tokenResult, // Store the full result separately
        role: userDetails?.role
      };
      
      // Set session and store backend session ID if available
      setSession(userSession);
      
      // If this is a new login response with sessionId (added by backend), store it
      const responseSessionId = (user as User & { sessionId?: string }).sessionId;
      if (responseSessionId) {
        localStorage.setItem(CURRENT_SESSION_KEY, responseSessionId);
      }
    } catch (error) {
      console.error("Error getting auth token:", error);
      setError("Failed to authenticate session");
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  });
};

/**
 * Students stay signed in through the NextAuth cookie even when Firebase
 * `currentUser` is null (custom-token sign-in failed, or auth was dropped
 * mid-session). Reading saves need a Firebase ID token. Wait until auth has
 * settled, then mint a fresh custom token from that cookie session if needed.
 */
let pendingFirebaseRestore: Promise<User | null> | null = null;

export async function ensureFirebaseUser(): Promise<User | null> {
  if (!auth) return null;
  try {
    if (typeof auth.authStateReady === 'function') await auth.authStateReady();
  } catch {
    // Persistence can fail on locked-down school devices. Fall through.
  }
  if (auth.currentUser) return auth.currentUser;
  if (!pendingFirebaseRestore) {
    pendingFirebaseRestore = (async () => {
      try {
        const res = await fetch('/api/auth/firebase-session-token', { method: 'POST' });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.token) return null;
        const cred = await signInWithCustomToken(auth, json.token);
        return cred.user;
      } catch {
        return null;
      } finally {
        pendingFirebaseRestore = null;
      }
    })();
  }
  return pendingFirebaseRestore;
}

/**
 * Get auth headers for API requests.
 * Canonical async implementation — fetches a fresh Firebase ID token.
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const user = await ensureFirebaseUser();
  if (!user) return { 'Content-Type': 'application/json' };
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Same as getAuthHeaders, and one retry with a forced token refresh when the
 * server rejects the first token. Reading heartbeats and the end-of-session
 * submit both go through this so a stale token does not drop the save.
 */
export async function fetchWithFirebaseAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const send = async (forceRefresh: boolean) => {
    const user = await ensureFirebaseUser();
    const headers = new Headers(init.headers || undefined);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (user) headers.set('Authorization', `Bearer ${await user.getIdToken(forceRefresh)}`);
    return fetch(url, { ...init, headers });
  };
  const first = await send(false);
  if (first.status !== 401) return first;
  return send(true);
}

/**
 * Never-throwing variant of getAuthHeaders for best-effort callers (e.g. TTS).
 * /api/tts sits behind middleware that 401s requests carrying no auth signal;
 * students authenticate via Firebase (no NextAuth cookie), so attach the
 * Firebase ID token. If the token fetch fails — or no user is signed in —
 * degrade to Content-Type-only headers rather than blocking the request.
 */
export const getAuthHeadersSafe = async (): Promise<Record<string, string>> => {
  try {
    return await getAuthHeaders();
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

/*
 * getFreshAuthHeaders was removed on 2026-09-06. It had no callers, and its
 * no-current-user branch set `X-Testing-Mode: true` -- a header the backend
 * treated as a full authentication bypass. Use getAuthHeaders (or
 * getAuthHeadersSafe) and let an unauthenticated request fail as one.
 */

/**
 * Refresh the auth token
 */
export const refreshAuthToken = async (): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No current user found for token refresh");
      return null;
    }
      // Force refresh the token
    const newToken = await currentUser.getIdToken(true);
    
    // Update the stored session with new token
    const storedSession = getUserSession();
    if (storedSession) {
      const updatedSession = {
        ...storedSession,
        token: newToken
      };
      saveUserSession(updatedSession);
    }
    
    return newToken;
  } catch (error) {
    console.error("Failed to refresh authentication token:", error);
    return null;
  }
};

/**
 * Get user details from Firestore
 */
async function getUserDetailsFromFirestore(user: User): Promise<{name?: string, role?: string, children?: string[], parents?: string[]} | null> {
  try {
    const db = getFirestore();
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        name: data.name,
        role: data.role,
        children: data.children || [],
        parents: data.parents || []
      };
    }
    
    // Fallback to check parents collection if not found in users
    const parentRef = doc(db, 'parents', user.uid);
    const parentDoc = await getDoc(parentRef);
    if (parentDoc.exists()) {
      const data = parentDoc.data();
      return {
        name: data.name,
        role: 'parent',
        children: data.children || []
      };
    }
    
    // Fallback to check students collection
    const studentRef = doc(db, 'students', user.uid);
    const studentDoc = await getDoc(studentRef);
    if (studentDoc.exists()) {
      const data = studentDoc.data();
      return {
        name: data.name,
        role: 'student',
        parents: data.parents || []
      };
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching user details:", error);
    return null;
  }
}

/**
 * Find user by userId (for child accounts)
 */
export const findUserByUserId = async (userId: string): Promise<string | null> => {
  try {
    const db = getFirestore();
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    return querySnapshot.docs[0].data().email;
  } catch (error) {
    console.error("Error finding user by userId:", error);
    return null;
  }
};

/**
 * Get user role from Firestore
 */
export const getUserRole = async (uid: string): Promise<string | null> => {
  try {
    const db = getFirestore();
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists() && userDoc.data().role) {
      return userDoc.data().role;
    }
    
    return null;
  } catch (error) {
    console.error("Error getting user role:", error);
    return null;
  }
};

/**
 * Sync Firebase auth state with local storage
 * This is crucial to fix the state mismatch issues
 */
export const syncAuthState = async (): Promise<UserSession | null> => {
  const currentUser = auth.currentUser;
  const storedSession = getUserSession();
  
  // Case 1: Firebase has user but localStorage doesn't
  if (currentUser && (!storedSession || storedSession.uid !== currentUser.uid)) {
    console.log("Syncing: Firebase has user but localStorage doesn't match");
    const token = await currentUser.getIdToken(true);
    const userDetails = await getUserDetailsFromFirestore(currentUser);
    
    const userSession: UserSession = {
      uid: currentUser.uid,
      email: currentUser.email,
      name: currentUser.displayName || userDetails?.name || null,
      token: token,
      role: userDetails?.role
    };
    
    saveUserSession(userSession);
    return userSession;
  }
  
  // Case 2: Firebase has no user but localStorage does
  if (!currentUser && storedSession) {
    console.log("Syncing: Firebase has no user but localStorage does");
    clearAuthData();
    return null;
  }
  
  // Case 3: Both have matching user, check if token needs refresh
  if (currentUser && storedSession && currentUser.uid === storedSession.uid) {
    console.log("Syncing: Both have matching user");
    
    // Token is older than 30 minutes, refresh it
    const tokenDate = new Date(storedSession.tokenTimestamp || 0);
    const now = new Date();
    const diffMinutes = (now.getTime() - tokenDate.getTime()) / (1000 * 60);
    
    if (diffMinutes > 30) {
      console.log("Token is older than 30 minutes, refreshing");
      await refreshAuthToken();
    }
  }
  
  return storedSession;
};
